import Image from "next/image";
import Link from "@/components/ui/Link";
import { Heart, Download } from "lucide-react";
import type { HashtagRecord } from "@/types/api";
import type { CardRecord } from "@/lib/posts-helpers";
import {
  formatCount,
  packCoverUrl,
  postPathFor,
  stickerThumbUrl,
  timeAgo,
} from "@/lib/posts-helpers";
import { SITE, siteUrl } from "@/lib/seo/site";
import { orderByStickerId, stickerCaptioner } from "@/lib/seo/image-caption";
import { CardShareToggle } from "@/components/share/CardShareToggle";
import { AppPromptTrigger } from "@/components/layout/AppPromptTrigger";
import { TagChipLink } from "@/components/stickers/TagChipLink";
import { CreatorLink } from "@/components/stickers/CreatorLink";
import { creatorPath } from "@/lib/deep-links";
import {
  StickerCardDesktopMeta,
  StickerCardDesktopBlurb,
  StickerCardDesktopTags,
} from "@/components/stickers/StickerCardDesktopExtras";

interface StickerCardProps {
  record: CardRecord;
  priority?: boolean;
  /** Index within the page; used to pick `priority` and `loading` defaults. */
  index?: number;
  /**
   * Heading level for the pack title, so the card slots into whatever heading
   * outline its host page has.
   *
   * Listing pages differ above the grid: `CategoryPage` renders an
   * `h2#category-intro`, so its cards are `h3` and the outline reads h1 → h2 →
   * h3. `HashtagRoute` deliberately marks its intro up as a `<p>` rather than
   * an `h2` (a heading with no section under it), which left h1 → h3 with the
   * h2 skipped — the one thing `heading-order` fails on, across ~10,000 URLs.
   * Those pages pass "h2" instead. Purely semantic: every level renders the
   * same class list, so the rendered text and its styling are identical.
   */
  titleAs?: "h2" | "h3";
}

const FALLBACK_AVATAR = `${SITE.cdn}/static-content/unknow-user.svg`;

function isHashtagRecord(
  r: CardRecord,
): r is CardRecord & Required<Pick<HashtagRecord, "stickers">> {
  return Array.isArray((r as HashtagRecord).stickers);
}

export function StickerCard({
  record,
  priority,
  index = 99,
  titleAs: Title = "h3",
}: StickerCardProps) {
  const cover = packCoverUrl(record);
  const path = postPathFor(record);
  // Only the first card is the LCP candidate above the fold on mobile.
  // Loading more than one eager image makes them compete for bandwidth and
  // pushes LCP later under throttled networks.
  const eager = priority ?? index === 0;
  // sticker_Id order before the cut, so the strip shows the pack's first five
  // and numbers them the way the detail page and the image sitemap do. That
  // only holds because the feed's preview IS the pack's first five by id —
  // sticko-api-v2 StickerPreviewService selects `ROW_NUMBER() OVER (PARTITION
  // BY post_Id ORDER BY sticker_Id) <= 5` — so "sticker 2" here and "sticker 2"
  // on the pack page name the same image. If that query ever ranks previews
  // differently, the ordinal in this strip's captions must go.
  const stickers = isHashtagRecord(record) ? orderByStickerId(record.stickers).slice(0, 5) : [];
  // Resolved once per card, not once per thumbnail.
  const caption = stickerCaptioner(record);
  // NOTE: the `unoptimized` and `quality` props below are currently INERT.
  // next.config.ts sets `images: { unoptimized: true }` globally, so every
  // next/image renders a bare <img> pointing at the full-resolution CDN file —
  // no srcset, no resizing, no re-encode. That makes `sizes` and `quality`
  // dead too, here and everywhere in this directory. It is why a 118KB avatar
  // is downloaded into a 35x35 box.
  //
  // They are kept, not deleted, because they are correct the moment image
  // optimization is turned back on, and they encode a real decision:
  // animated webp cannot be optimized (next/image flattens it to one frame),
  // EXCEPT for the LCP card, where a raw 200KB-1MB animation dominates mobile
  // LCP — there we would serve a static optimized frame and let the visitor
  // see the animation on the detail page.
  //
  // Don't read the props below as evidence this site optimizes images. It does
  // not. See the "Deliberately deferred" notes on Cloudflare Image Resizing /
  // Vercel Image Transformations.
  const isAnimated = Number(record.is_animated) === 1;
  const tags = Array.from(
    new Set(
      (record.post_tags ?? "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    ),
  );
  const avatarSrc = record.profile_photo || FALLBACK_AVATAR;
  const creator = creatorPath(record.user_Id);

  return (
    <article
      className={`rounded-card overflow-hidden bg-white shadow-(--shadow-card)${
        // Below the first screen, let the browser skip layout and paint of the
        // card until it nears the viewport. The markup — links, titles, alt
        // text and every image src — stays in the server HTML for crawlers;
        // only the rendering work (and, in Chrome, the lazy image fetches)
        // waits. The intrinsic size reserves roughly a card's height so the
        // scrollbar does not jump; `auto` remembers the real height once seen.
        index >= 2 ? "[contain-intrinsic-size:auto_340px] [content-visibility:auto]" : ""
      }`}
    >
      {/* Two links where there used to be one. The whole header and strip were
          a single link to the pack, so the author could not also link to their
          profile — an <a> inside an <a> is invalid HTML and browsers split it
          unpredictably. Now the title links to the pack and the author line to
          /u/<id>; the strip below is a second, pointer-only route to the pack.
          Classes are unchanged, so nothing moves visually.

          No aria-label on the title link: its accessible name is the visible
          title, which keeps WCAG 2.5.3 (label-content-name-mismatch) satisfied
          the same way the old whole-header link did. */}
      <header className="flex items-center gap-3 px-4 pt-4">
        {/* Duplicates the name link beside it, so it is kept out of the tab
            order and the accessibility tree: one creator link per card. */}
        <CreatorLink
          href={creator}
          className="bg-muted relative inline-block h-8.75 w-8.75 shrink-0 overflow-hidden rounded-full"
          tabIndex={-1}
          aria-hidden
        >
          <Image
            src={avatarSrc}
            alt={record.user_name || "Sticker pack author"}
            fill
            sizes="35px"
            className="object-cover"
            unoptimized={avatarSrc.endsWith(".svg")}
          />
        </CreatorLink>
        <div className="min-w-0">
          {/* Title and author are stacked 19px lines, and each is now its own
              link — too small for WCAG 2.5.8 / Lighthouse `target-size` (24px).
              Each hit area grows AWAY from the other by padding plus an equal
              negative margin (title up into the card padding, author down),
              so both reach 24px without overlapping and nothing moves. The
              truncation moved from the heading onto the link with it, so the
              ellipsis renders exactly as before. */}
          <Title className="text-foreground text-[16px] leading-tight font-medium">
            <Link href={path} className="-mt-1.25 block truncate pt-1.25">
              {record.post_title}
            </Link>
          </Title>
          <CreatorLink
            href={creator}
            className="text-muted-foreground -mb-1.25 block truncate pb-1.25 text-[13px] hover:underline"
          >
            {record.user_name}
          </CreatorLink>
        </div>
      </header>

      {/* The title link above is the keyboard and screen-reader route to the
          pack; this one only keeps the thumbnails clickable, so it is out of
          the tab order and the accessibility tree. The image alt text is still
          in the HTML for crawlers. */}
      <Link href={path} className="block" tabIndex={-1} aria-hidden>
        {stickers.length > 0 ? (
          <ul className="mt-3 flex gap-1.5 px-4">
            {stickers.map((s, i) => {
              const src = stickerThumbUrl(s.sticker_name);
              return (
                <li
                  key={s.sticker_Id}
                  className="bg-muted relative aspect-square flex-1 overflow-hidden rounded-md"
                >
                  {src ? (
                    <Image
                      src={src}
                      alt={caption(i + 1)}
                      fill
                      sizes="(min-width: 600px) 110px, 18vw"
                      className="object-contain p-1"
                      // Eager, but no `priority`: that emits a High-priority
                      // <link rel="preload"> for a ~100 KB animated thumbnail,
                      // and the LCP on every listing is the intro text
                      // (h2#category-intro / p#hashtag-intro on mobile, the
                      // card blurb on desktop — measured 2026-10-03). The
                      // preload only competed with it for bandwidth.
                      loading={eager && i === 0 ? "eager" : "lazy"}
                      unoptimized={isAnimated}
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="bg-muted relative mt-3 aspect-5/2 w-full">
            <Image
              src={cover}
              alt={record.post_title}
              fill
              sizes="(min-width: 600px) 568px, 100vw"
              className="object-cover"
              priority={eager}
              fetchPriority={eager ? "high" : "auto"}
              loading={eager ? "eager" : "lazy"}
              // First card: always optimize to avoid raw animated webp blowing LCP.
              unoptimized={isAnimated && !eager}
              quality={eager ? 75 : 70}
            />
          </div>
        )}
      </Link>

      <div className="flex flex-col gap-2.5 px-4 pt-3 pb-4">
        <div className="text-ink flex flex-wrap items-center gap-7">
          <AppPromptTrigger intent="like" ariaLabel="Like sticker pack">
            <Heart className="h-7 w-7" strokeWidth={1.6} />
          </AppPromptTrigger>
          <AppPromptTrigger intent="download" ariaLabel="Download sticker pack">
            <Download className="h-6 w-6" strokeWidth={1.8} />
          </AppPromptTrigger>
          <CardShareToggle url={siteUrl(path)} title={record.post_title} />
        </div>

        <ul className="text-foreground flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
          <li>{formatCount(record.post_like)} Likes</li>
          <li aria-hidden className="bg-foreground/60 h-1 w-1 rounded-full" />
          <li>{formatCount(record.post_downloads)} Download</li>
          {/* Desktop-only extras (comments, sticker count, pack size) render
              client-side via matchMedia so they stay out of the SSR'd DOM on
              mobile — Lighthouse's mobile dom-size audit counts hidden-via-CSS
              nodes, and the verbose extras pushed it over the 1500 threshold. */}
          <StickerCardDesktopMeta record={record} />
        </ul>

        <StickerCardDesktopBlurb record={record} path={path} />

        {tags.length > 0 ? (
          <div className="flex flex-wrap gap-x-1.5 gap-y-1 text-[14px]">
            {tags.slice(0, 5).map((tag) => (
              <TagChipLink key={tag} tag={tag} className="text-link hover:underline">
                #{tag}
              </TagChipLink>
            ))}
            <StickerCardDesktopTags tags={tags.slice(5)} />
          </div>
        ) : null}

        <span className="text-muted-foreground text-[13px]">{timeAgo(record.created_on)}</span>
      </div>
    </article>
  );
}
